import React, { FC, ReactNode, useMemo } from 'react';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Alert, Button, Col, Row, Spin, Typography } from 'antd';

import { required } from '../../../../../utils/validation';
import useRequestHandler from '../../../../../hooks/useRequestHandler';

import FormBase from '../../../../../components_/Form';
import FormAntButton from '../FormAntButton';
import FormAntInput from '../FormAntInput';
import FormTree from '../FromTree';
import { SecTreeToolbarActions } from '../../SecTreePageHeader';
import { getNodeLabel, ITreeNode } from '../../treeUtils';
import { LoadedSecurableTypes } from '../../../../../utils/parseSecurableTypes';
import { IProps as IFormProps } from './index';
import { IRole } from '../../../../../models/types';
import List from './List';

import '../PermissionsTable.css';

type IValues = IRole;

type IRender = (props: FormRenderProps<IValues>) => ReactNode;

export type LoadedPermissions = LoadedSecurableTypes;

interface IProps {
  data: IFormProps['data'];
  item: IValues | null;
  secTreeHierarchy: ITreeNode | Record<string, unknown>;
  isCreate: boolean;
  isEdit: boolean;
  isCopy: boolean;
  treeItem: string | null;
  loadedPermissions: LoadedPermissions | null;
  isPermissionsLoading: boolean;
  isPermissionsError: boolean;
  onSubmitAction: (values: IValues) => Promise<unknown>;
  onCancel: () => void;
}

const defaultValues: IValues = {
  id: null,
  name: '',
  friendlyName: '',
  actions: {}
};

const Form: FC<IProps> = ({
  data,
  item,
  isCreate,
  isEdit,
  isCopy,
  treeItem,
  loadedPermissions,
  isPermissionsLoading,
  isPermissionsError,
  onSubmitAction,
  onCancel,
  secTreeHierarchy
}) => {
  const { nodeACL } = data;

  const requestHandler = useRequestHandler();
  const showOverlay = Boolean(loadedPermissions) && isPermissionsLoading;

  const displayTreeItem =
    isPermissionsLoading && loadedPermissions ? loadedPermissions.treeItem : treeItem;

  const panelTitle = useMemo(
    () => (displayTreeItem ? getNodeLabel(secTreeHierarchy, displayTreeItem) : ''),
    [secTreeHierarchy, displayTreeItem]
  );

  const initialValues: IValues = {
    ...defaultValues,
    ...((isEdit || isCopy) && {
      ...item,
      actions: nodeACL
    }),
    ...(isCopy && {
      id: defaultValues.id,
      name: defaultValues.name
    })
  };

  const submitButtonTitle = isEdit ? 'Сохранить' : 'Добавить';

  const onSubmit = async (values: IValues) => {
    const error = await onSubmitAction(values);

    requestHandler({
      error,
      entity: 'role',
      onSuccess: onCancel,
      isCreate,
      isEdit,
      isCopy
    });
  };

  const render: IRender = ({ handleSubmit }) => (
    <FormBase style={{ height: '100%' }} onSubmit={handleSubmit}>
      <Row gutter={24} style={{ marginTop: 40, marginBottom: 24 }}>
        <Col flex="0 0 25%" style={{ minWidth: 0 }}>
          <FormAntInput
            name="friendlyName"
            label="Название"
            placeholder="Введите название"
            useFieldConfig={{ validate: required }}
          />
        </Col>
      </Row>
      <div className="sec-tree-role-permissions-row">
        <div className="sec-tree-layout">
          <div className="sec-tree-layout__tree">
            <FormTree defaultSelected={treeItem} secTreeHierarchy={secTreeHierarchy} />
          </div>
          <div className="sec-tree-layout__content">
            {treeItem && (
              <div className="sec-tree-permissions-card sec-tree-permissions-panel-content">
                <SecTreeToolbarActions>
                  <Button onClick={onCancel}>Отмена</Button>
                  <FormAntButton>{submitButtonTitle}</FormAntButton>
                </SecTreeToolbarActions>
                {isPermissionsError && loadedPermissions && (
                  <Alert
                    type="error"
                    message="Ошибка сервера"
                    showIcon
                    className="sec-tree-permissions-error"
                  />
                )}
                <div className="sec-tree-permissions-header">
                  <Typography.Title level={4} className="sec-tree-permissions-title">
                    {panelTitle}
                  </Typography.Title>
                </div>
                <fieldset
                  className="sec-tree-permissions-fieldset"
                  disabled={isPermissionsLoading}
                >
                  {loadedPermissions && (
                    <List
                      key={loadedPermissions.treeItem}
                      treeItem={loadedPermissions.treeItem}
                      list={loadedPermissions.securableTypes}
                    />
                  )}
                </fieldset>
                {showOverlay && (
                  <div className="sec-tree-permissions-overlay">
                    <Spin size="large" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </FormBase>
  );

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={render}
    />
  );
};

export default Form;
